import { useEffect, useMemo, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { usePickers } from '../hooks/usePickers';
import { useStores } from '../hooks/useStores';
import type { Picker } from '../types';

type NewPickerForm = {
  first_name: string;
  last_name: string;
  store_id: string;
};

export default function Pickers() {
  const { pickers, fetchPickers, createPicker, deletePicker } = usePickers();
  const { stores, fetchStores } = useStores();

  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pickerToDelete, setPickerToDelete] = useState<Picker | null>(null);
  const [newPicker, setNewPicker] = useState<NewPickerForm>({
    first_name: '',
    last_name: '',
    store_id: '',
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchPickers(), fetchStores()]);
      setLoading(false);
    };
    void load();
  }, [fetchPickers, fetchStores]);


  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pickers;
    return pickers.filter((picker) => {
      const fullName = `${picker.first_name ?? ''} ${picker.last_name ?? ''}`.toLowerCase();
      const storeName = (picker.store?.name ?? '').toLowerCase();
      return fullName.includes(q) || storeName.includes(q);
    });
  }, [pickers, query]);

  const openNew = () => {
    setNewPicker({ first_name: '', last_name: '', store_id: '' });
    setIsCreateOpen(true);
  };

  const submitNewPicker = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const created = await createPicker({
        first_name: newPicker.first_name.trim(),
        last_name:  newPicker.last_name.trim(),
        store_id:   newPicker.store_id,
      });
      if (created) {
        setIsCreateOpen(false);
        setNewPicker({ first_name: '', last_name: '', store_id: '' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeletePicker = async () => {
    if (!pickerToDelete || submitting) return;
    setSubmitting(true);
    try {
      const deleted = await deletePicker(String(pickerToDelete.id));
      if (deleted) setPickerToDelete(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
      <div className="space-y-6">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-stone-900">Pickers</h1>
            <p className="text-sm text-stone-500 mt-1">Manage pickers and keep the team list up to date.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
                placeholder="Search picker or store…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#ee2b74]"
            />
            <button
                onClick={openNew}
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-white bg-[#ee2b74] hover:bg-[#d4245f] transition shadow-sm"
            >
              New Picker
            </button>
          </div>
        </header>

        {/* Table */}
        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
          <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
            <h2 className="font-semibold text-stone-900">All Pickers</h2>
            <span className="text-sm text-stone-500">{filtered.length} total</span>
          </div>

          {loading ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {['Full Name', 'Store', 'Created', 'Actions'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                    ))}
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {Array.from({ length: 4 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="px-4 py-3"><div className="h-4 w-36 bg-stone-100 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-24 bg-stone-100 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-20 bg-stone-100 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-7 w-16 bg-stone-100 rounded ml-auto" /></td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          ) : filtered.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-stone-500">
                {query ? `No pickers match "${query}"` : 'No pickers yet. Add one to get started.'}
              </div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Full Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Store</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Actions</th>
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                  {filtered.map((picker) => (
                      <tr key={picker.id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-stone-900">
                          {picker.first_name} {picker.last_name}
                        </td>
                        <td className="px-4 py-3 text-stone-600">{picker.store?.name ?? '—'}</td>
                        <td className="px-4 py-3 text-stone-500 text-xs">
                          {picker.created_at ? new Date(picker.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                              onClick={() => setPickerToDelete(picker)}
                              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 border border-red-200 bg-white text-xs text-red-600 hover:bg-red-50 transition"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}
        </section>

        {/* Create modal */}
        {isCreateOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
              <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-stone-200 overflow-hidden">
                <div className="h-1 w-full bg-[#ee2b74]" />
                <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
                  <h3 className="font-semibold text-stone-900 text-lg">New Picker</h3>
                  <button onClick={() => setIsCreateOpen(false)} className="text-stone-400 hover:text-stone-600 text-xl leading-none">✕</button>
                </div>
                <form onSubmit={submitNewPicker} className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">First name</label>
                      <input
                          required
                          className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none"
                          value={newPicker.first_name}
                          onChange={(e) => setNewPicker(p => ({ ...p, first_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1.5">Last name</label>
                      <input
                          required
                          className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none"
                          value={newPicker.last_name}
                          onChange={(e) => setNewPicker(p => ({ ...p, last_name: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1.5">Store</label>
                    <select
                        required
                        className="w-full rounded-lg border border-stone-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#ee2b74] outline-none"
                        value={newPicker.store_id}
                        onChange={(e) => setNewPicker(p => ({ ...p, store_id: e.target.value }))}
                    >
                      <option value="">— Select a store —</option>
                      {stores.map((store) => (
                          <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
                    <button type="button" onClick={() => setIsCreateOpen(false)} disabled={submitting}
                            className="rounded-lg border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                      Cancel
                    </button>
                    <button type="submit" disabled={submitting}
                            className="rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition">
                      {submitting ? 'Creating…' : 'Create Picker'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
        )}

        {/* Delete modal */}
        {pickerToDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
              <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl border border-stone-200 overflow-hidden">
                <div className="h-1 w-full bg-red-500" />
                <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
                  <h3 className="font-semibold text-stone-900 text-lg">Delete Picker</h3>
                  <button onClick={() => setPickerToDelete(null)} className="text-stone-400 hover:text-stone-600 text-xl leading-none">✕</button>
                </div>
                <div className="p-5 space-y-4">
                  <p className="text-sm text-stone-600">
                    Are you sure you want to delete{' '}
                    <span className="font-semibold text-stone-900">
                                    {pickerToDelete.first_name} {pickerToDelete.last_name}
                                </span>?
                    This cannot be undone.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setPickerToDelete(null)} disabled={submitting}
                            className="rounded-lg border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                      Cancel
                    </button>
                    <button type="button" onClick={confirmDeletePicker} disabled={submitting}
                            className="rounded-lg bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50 transition">
                      {submitting ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}
      </div>
  );
}